import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { 
  ArrowLeft,
  CheckCircle, 
  XCircle, 
  Ban, 
  Trash2, 
  Calendar as CalendarIcon,
  Mail,
  Phone,
  MapPin,
  Building2,
  FileText,
  Briefcase,
  Hash,
  User,
  Edit,
  Download,
  Eye,
  Search,
  Filter,
  MoreVertical,
  Clock,
  Award,
  Shield,
  TrendingUp,
  Activity,
  ChevronDown,
  ChevronRight,
  Users,
  FileImage,
  FolderOpen,
  Star,
  Check,
  AlertCircle,
  Info
} from "lucide-react";
import { format } from "date-fns";
import { ar, enUS } from "date-fns/locale";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Label } from "@/components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Separator } from "@/components/ui/separator";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { ScrollArea } from "@/components/ui/scroll-area";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import OptimizedImage from "@/components/ui/OptimizedImage";
import { useLanguage } from "@/contexts/LanguageContext";
import { useAuth } from "@/contexts/AuthContext";
import { useToast } from "@/hooks/use-toast";
import { useAdminClient, useUpdateClientStatus, useDeleteClient } from "@/hooks/api/use-admin";
import { adminService } from "@/api/services/admin.service";
import { queryClient } from "@/lib/query-client";
import { queryKeys } from "@/hooks/query-keys";
import { cn } from "@/lib/utils";

const AdminClientDetails = () => {
  const { t, language, isRTL } = useLanguage();
  const { user } = useAuth();
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const isAdmin = user?.type === 'admin';
  const [activeTab, setActiveTab] = useState('overview');
  const { toast } = useToast();

  const { data: clientDetails, isLoading, error } = useAdminClient(
    parseInt(id || '0'),
    { enabled: isAdmin && !!id }
  );

  const updateStatusMutation = useUpdateClientStatus();
  const deleteMutation = useDeleteClient();

  const getStatusBadge = (status: string) => {
    const statusConfig = {
      pending: {
        label: t('common.pending') || 'قيد المراجعة',
        className: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200 border-yellow-200',
        icon: <Clock size={14} />
      },
      approved: {
        label: t('common.approved') || 'معتمد',
        className: 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200 border-green-200',
        icon: <CheckCircle size={14} />
      },
      rejected: {
        label: t('common.rejected') || 'مرفوض',
        className: 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200 border-red-200',
        icon: <XCircle size={14} />
      },
      suspended: {
        label: t('admin.clients.suspended') || 'معلق',
        className: 'bg-orange-100 text-orange-800 dark:bg-orange-900 dark:text-orange-200 border-orange-200',
        icon: <Ban size={14} />
      },
    };

    const config = statusConfig[status as keyof typeof statusConfig] || statusConfig.pending;

    return (
      <Badge className={cn("flex items-center gap-1 px-3 py-1 border", config.className)}>
        {config.icon}
        {config.label}
      </Badge>
    );
  };

  const handleUpdateStatus = (status: 'approved' | 'rejected' | 'suspended') => {
    if (!clientDetails?.data) return;
    
    const clientId = (clientDetails.data as any).client_id || (clientDetails.data as any).id;
    
    updateStatusMutation.mutate(
      { id: clientId, data: { status } },
      {
        onSuccess: () => {
          // Refetch client details to update status
          // The query will automatically refetch due to stale time
        },
      }
    );
  };

  const handleDelete = () => {
    if (!clientDetails?.data) return;
    
    const clientId = (clientDetails.data as any).client_id || (clientDetails.data as any).id;
    
    if (confirm(t('admin.clients.confirmDelete'))) {
      deleteMutation.mutate(clientId, {
        onSuccess: () => {
          navigate('/admin/dashboard/clients');
        },
      });
    }
  };

  const handleEdit = () => {
    if (!clientDetails?.data) return;
    navigate(`/admin/dashboard/clients/${(clientDetails.data as any).client_id || (clientDetails.data as any).id}/edit`);
  };

  const handleApproveDocument = async (documentId: number, status: 'approved' | 'rejected') => {
    if (!id) return;
    
    try {
      await adminService.approveDocument(parseInt(id), documentId, status);
      // Refetch client details using correct query key
      queryClient.invalidateQueries({ queryKey: queryKeys.adminClients.detail(parseInt(id)) });
      toast({
        title: t('admin.clients.documentUpdated') || 'تم تحديث المستند',
        description: status === 'approved' 
          ? (t('admin.clients.documentApproved') || 'تم اعتماد المستند بنجاح')
          : (t('admin.clients.documentRejected') || 'تم رفض اعتماد المستند'),
      });
    } catch (error) {
      toast({
        title: t('common.error') || 'خطأ',
        description: t('admin.clients.documentUpdateFailed') || 'فشل تحديث المستند',
        variant: 'destructive',
      });
    }
  };

  // Only admin can access
  if (!isAdmin) {
    return (
      <div className="space-y-6">
        <div className="text-center py-12">
          <p className="text-muted-foreground">
            {t('admin.clients.adminOnly') || 'هذه الصفحة متاحة فقط للمسؤولين'}
          </p>
        </div>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="space-y-6">
        <div className="flex items-center gap-4">
          <Skeleton className="h-10 w-10" />
          <Skeleton className="h-8 w-48" />
        </div>
        <div className="space-y-4">
          <Skeleton className="h-32 w-full" />
          <Skeleton className="h-32 w-full" />
          <Skeleton className="h-32 w-full" />
        </div>
      </div>
    );
  }

  if (error || !clientDetails?.data) {
    return (
      <div className="space-y-6">
        <div className="text-center py-12">
          <p className="text-muted-foreground">
            {t('admin.clients.notFound') || 'العميل غير موجود'}
          </p>
          <Button 
            variant="outline" 
            onClick={() => navigate('/admin/dashboard/clients')}
            className="mt-4"
          >
            {t('common.back') || 'العودة'}
          </Button>
        </div>
      </div>
    );
  }

  // Extract nested data from API response structure
  const responseData = (clientDetails.data as any).data || clientDetails.data;
  const clientData = responseData?.client || responseData;
  const userData = responseData?.user || clientData?.user || responseData;
  const clientId = responseData?.client_id || clientData?.id || (clientDetails.data as any).client_id || (clientDetails.data as any).id;
  const clientStatus = clientData?.status || responseData?.status || (clientDetails.data as any).status || 'pending';
  const specializations = Array.isArray(responseData?.specializations) ? responseData.specializations : (Array.isArray(clientData?.specializations) ? clientData.specializations : []);
  const projects = Array.isArray(clientData?.projects) ? clientData.projects : (Array.isArray(responseData?.projects) ? responseData.projects : []);
  // Profile image and documents are at root level, not nested
  const profileImageUrl = responseData?.profile_image_url || clientData?.profile_image_url || (clientDetails.data as any).profile_image_url;
  const documents = Array.isArray(responseData?.documents) ? responseData.documents : (Array.isArray(clientData?.documents) ? clientData.documents : (Array.isArray((clientDetails.data as any).documents) ? (clientDetails.data as any).documents : []));

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-slate-900 overflow-x-hidden" dir={isRTL ? 'rtl' : 'ltr'}>
      {/* Enhanced Header */}
      <div className="bg-white dark:bg-slate-800 border-b border-slate-200 dark:border-slate-700">
        <div className="w-full px-3 sm:px-4 md:px-6 lg:px-8 py-3 sm:py-4 md:py-6">
          <div className={`flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between ${isRTL ? 'flex-row-reverse' : ''}`}>
            <div className={`flex items-center gap-2 sm:gap-3 md:gap-4 min-w-0 ${isRTL ? 'flex-row-reverse' : ''}`}>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => navigate('/admin/dashboard/clients')}
                className="flex items-center gap-1 sm:gap-2 hover:bg-slate-100 dark:hover:bg-slate-700 flex-shrink-0"
              >
                <ArrowLeft size={14} className={`sm:w-4 sm:h-4 ${isRTL ? 'ml-2' : 'mr-2'}`} />
                <span className="text-xs sm:text-sm">{t('common.back') || 'العودة'}</span>
              </Button>
              <div className="hidden sm:block h-4 sm:h-6 w-px bg-slate-200 dark:bg-slate-600 flex-shrink-0" />
              <div className="min-w-0 flex-1">
                <h1 className="text-lg sm:text-xl md:text-2xl font-bold text-slate-900 dark:text-slate-100 truncate">
                  {t('admin.clients.clientDetails') || 'تفاصيل العميل'}
                </h1>
                <p className="text-xs sm:text-sm text-slate-500 dark:text-slate-400 mt-1 truncate">
                  #{clientId} • {userData?.name || responseData?.name || (clientDetails.data as any).name}
                </p>
              </div>
            </div>
            
            <div className={`flex items-center gap-2 flex-shrink-0 ${isRTL ? 'flex-row-reverse' : ''}`}>
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="outline" size="sm" className="flex items-center gap-1 sm:gap-2 min-w-[80px] sm:min-w-[100px] justify-center">
                    <MoreVertical size={12} className={`sm:w-4 sm:h-4 ${isRTL ? 'ml-2' : 'mr-2'}`} />
                    <span className="text-xs sm:text-sm hidden xs:inline">{t('common.actions') || 'إجراءات'}</span>
                    <span className="text-xs sm:text-sm xs:hidden">...</span>
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align={isRTL ? "start" : "end"} className="w-48">
                  <DropdownMenuItem onClick={handleEdit} className={`flex items-center gap-2 ${isRTL ? 'flex-row-reverse' : ''}`}>
                    <Edit size={14} className={isRTL ? 'ml-2' : 'mr-2'} />
                    {t('admin.clients.edit') || 'تعديل'}
                  </DropdownMenuItem>
                  {clientStatus === 'pending' && (
                    <>
                      <DropdownMenuItem onClick={() => handleUpdateStatus('approved')} className={`flex items-center gap-2 text-green-600 ${isRTL ? 'flex-row-reverse' : ''}`}>
                        <CheckCircle size={14} className={isRTL ? 'ml-2' : 'mr-2'} />
                        {t('admin.clients.approve') || 'اعتماد'}
                      </DropdownMenuItem>
                      <DropdownMenuItem onClick={() => handleUpdateStatus('rejected')} className={`flex items-center gap-2 text-red-600 ${isRTL ? 'flex-row-reverse' : ''}`}>
                        <XCircle size={14} className={isRTL ? 'ml-2' : 'mr-2'} />
                        {t('admin.clients.reject') || 'رفض'}
                      </DropdownMenuItem>
                    </>
                  )}
                  {clientStatus === 'approved' && (
                    <DropdownMenuItem onClick={() => handleUpdateStatus('suspended')} className={`flex items-center gap-2 text-orange-600 ${isRTL ? 'flex-row-reverse' : ''}`}>
                      <Ban size={14} className={isRTL ? 'ml-2' : 'mr-2'} />
                      {t('admin.clients.suspend') || 'تعليق'}
                    </DropdownMenuItem>
                  )}
                  {clientStatus === 'suspended' && (
                    <DropdownMenuItem onClick={() => handleUpdateStatus('approved')} className={`flex items-center gap-2 text-green-600 ${isRTL ? 'flex-row-reverse' : ''}`}>
                      <CheckCircle size={14} className={isRTL ? 'ml-2' : 'mr-2'} />
                      {t('admin.clients.reactivate') || 'إعادة تفعيل'}
                    </DropdownMenuItem>
                  )}
                  <DropdownMenuItem onClick={handleDelete} className={`flex items-center gap-2 text-red-600 ${isRTL ? 'flex-row-reverse' : ''}`}>
                    <Trash2 size={14} className={isRTL ? 'ml-2' : 'mr-2'} />
                    {t('admin.clients.delete') || 'حذف'}
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </div>
        </div>
      </div>

      {/* Enhanced Profile Hero Section */}
      <div className="w-full px-3 sm:px-4 md:px-6 lg:px-8 py-4 sm:py-6 md:py-8">
        <div className="bg-white dark:bg-slate-800 rounded-xl sm:rounded-2xl shadow-lg sm:shadow-xl border border-slate-200 dark:border-slate-700 overflow-hidden">
          {/* Hero Background with Gradient */}
          <div className="relative h-16 sm:h-20 md:h-24 bg-gradient-to-r from-primary via-primary/90 to-primary/80">
            <div className="absolute inset-0 bg-gradient-to-br from-transparent via-primary/20 to-primary/30"></div>
            {/* Decorative Elements */}
            <div className="absolute top-2 right-2 sm:top-4 sm:right-4 w-8 h-8 sm:w-16 sm:h-16 bg-white/10 rounded-full blur-xl"></div>
            <div className="absolute bottom-2 left-2 sm:bottom-4 sm:left-4 w-12 h-12 sm:w-24 sm:h-24 bg-white/5 rounded-full blur-2xl"></div>
          </div>
          
          <div className="px-3 sm:px-4 md:px-6 lg:px-8 pb-4 sm:pb-6 md:pb-8">
            <div className="flex flex-col items-center gap-4 sm:gap-6 -mt-8 sm:-mt-12">
              {/* Enhanced Avatar */}
              <div className="relative group">
                <Avatar className="w-16 h-16 sm:w-20 sm:h-20 md:w-24 md:h-24 border-2 sm:border-3 border-white dark:border-slate-800 shadow-lg sm:shadow-xl ring-1 sm:ring-2 ring-primary/20 transition-all duration-300 group-hover:ring-primary/40">
                  <AvatarImage src={profileImageUrl} alt={userData?.name || 'Profile'} className="object-cover" />
                  <AvatarFallback className="text-base sm:text-lg md:text-xl font-bold bg-gradient-to-br from-primary to-primary/80 text-white shadow-inner">
                    {(userData?.name || responseData?.name || (clientDetails.data as any).name || 'U')?.charAt(0).toUpperCase()}
                  </AvatarFallback>
                </Avatar>
                {/* Online Status Indicator */}
                <div className="absolute bottom-1 right-1 sm:bottom-1.5 sm:right-1.5 w-2.5 h-2.5 sm:w-3 sm:h-3 md:w-4 md:h-4 bg-green-500 border-1.5 sm:border-2 border-white dark:border-slate-800 rounded-full shadow-lg">
                  <div className="w-full h-full bg-green-500 rounded-full animate-pulse"></div>
                </div>
              </div>
              
              {/* Enhanced Profile Info */}
              <div className="flex-1 text-center w-full px-2">
                <div className="flex flex-col items-center gap-2 sm:gap-3 mb-3">
                  <h2 className="text-lg sm:text-xl md:text-2xl font-bold text-slate-900 dark:text-slate-100 tracking-tight text-center break-words">
                    {userData?.name || responseData?.name || (clientDetails.data as any).name}
                  </h2>
                  <div className="flex items-center justify-center">
                    {getStatusBadge(clientStatus)}
                  </div>
                </div>
                
                <p className="text-sm sm:text-base md:text-lg text-slate-600 dark:text-slate-300 mb-3 font-medium text-center">
                  {clientData?.company_name || t('admin.clients.client') || 'عميل'}
                </p>
                
                {/* Enhanced Contact Info */}
                <div className="flex flex-wrap items-center justify-center gap-1 sm:gap-2 text-xs sm:text-sm text-slate-500 dark:text-slate-400 mb-4">
                  <span className="flex items-center gap-1 sm:gap-2 px-1.5 sm:px-2 py-1.5 bg-slate-100 dark:bg-slate-700 rounded-full">
                    <Mail size={10} className="text-primary flex-shrink-0" />
                    <span className="truncate">{userData?.email || responseData?.email || (clientDetails.data as any).email}</span>
                  </span>
                  {clientData?.phone && (
                    <span className="flex items-center gap-1 sm:gap-2 px-1.5 sm:px-2 md:px-3 py-1.5 bg-slate-100 dark:bg-slate-700 rounded-full max-w-[100px] xs:max-w-none">
                      <Phone size={10} className="text-primary flex-shrink-0" />
                      <span className="truncate">{clientData.phone}</span>
                    </span>
                  )}
                  {clientData?.address && (
                    <span className="flex items-center gap-1 sm:gap-2 px-1.5 sm:px-2 md:px-3 py-1.5 bg-slate-100 dark:bg-slate-700 rounded-full max-w-[100px] xs:max-w-none">
                      <MapPin size={10} className="text-primary flex-shrink-0" />
                      <span className="truncate">{clientData.address}</span>
                    </span>
                  )}
                </div>

                {/* Stats Row */}
                <div className="flex flex-wrap items-center justify-center gap-2 sm:gap-3 md:gap-4 text-xs sm:text-sm">
                  <div className="flex items-center gap-1.5 sm:gap-2">
                    <div className="w-4 h-4 sm:w-5 sm:h-5 md:w-6 md:h-6 bg-blue-100 dark:bg-blue-900 rounded-lg flex items-center justify-center">
                      <Briefcase size={10} className="text-blue-600 dark:text-blue-300" />
                    </div>
                    <span className="text-slate-600 dark:text-slate-300">
                      {specializations.length} {t('admin.clients.specializations') || 'تخصصات'}
                    </span>
                  </div>
                  <div className="flex items-center gap-1.5 sm:gap-2">
                    <div className="w-4 h-4 sm:w-5 sm:h-5 md:w-6 md:h-6 bg-green-100 dark:bg-green-900 rounded-lg flex items-center justify-center">
                      <FileText size={10} className="text-green-600 dark:text-green-300" />
                    </div>
                    <span className="text-slate-600 dark:text-slate-300">
                      {documents.length} {t('admin.clients.documents') || 'وثائق'}
                    </span>
                  </div>
                  <div className="flex items-center gap-1.5 sm:gap-2">
                    <div className="w-4 h-4 sm:w-5 sm:h-5 md:w-6 md:h-6 bg-purple-100 dark:bg-purple-900 rounded-lg flex items-center justify-center">
                      <Award size={10} className="text-purple-600 dark:text-purple-300" />
                    </div>
                    <span className="text-slate-600 dark:text-slate-300">
                      {projects.length} {t('admin.clients.projects') || 'مشاريع'}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Enhanced Tabbed Content Section */}
      <div className="w-full px-3 sm:px-4 md:px-6 lg:px-8 pb-6 sm:pb-8">
        <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-4 sm:space-y-6" dir={isRTL ? 'rtl' : 'ltr'}>
          <TabsList className={`grid w-full grid-cols-2 sm:grid-cols-4 bg-white border border-gray-200 rounded-lg p-1 ${isRTL ? 'grid-cols-[1fr_1fr_1fr_1fr]' : ''}`}>
            <TabsTrigger 
              value="overview" 
              className={`flex items-center space-x-1 sm:space-x-2 data-[state=active]:bg-primary data-[state=active]:text-white ${isRTL ? 'flex-row-reverse' : ''}`}
            >
              <User className="h-3 w-3 sm:h-4 sm:w-4" />
              <span className="text-xs sm:text-sm">{t('admin.clients.overview') || 'نظرة عامة'}</span>
            </TabsTrigger>
            <TabsTrigger 
              value="specializations" 
              className={`flex items-center space-x-1 sm:space-x-2 data-[state=active]:bg-primary data-[state=active]:text-white ${isRTL ? 'flex-row-reverse' : ''}`}
            >
              <Briefcase className="h-3 w-3 sm:h-4 sm:w-4" />
              <span className="text-xs sm:text-sm">{t('admin.clients.specializations') || 'التخصصات'}</span>
              <span className="bg-white/20 text-white px-1.5 py-0.5 rounded-full text-xs font-semibold">
                {specializations.length}
              </span>
            </TabsTrigger>
            <TabsTrigger 
              value="documents" 
              className={`flex items-center space-x-1 sm:space-x-2 data-[state=active]:bg-primary data-[state=active]:text-white ${isRTL ? 'flex-row-reverse' : ''}`}
            >
              <FileText className="h-3 w-3 sm:h-4 sm:w-4" />
              <span className="text-xs sm:text-sm">{t('admin.clients.documents') || 'الوثائق'}</span>
              <span className="bg-white/20 text-white px-1.5 py-0.5 rounded-full text-xs font-semibold">
                {documents.length}
              </span>
            </TabsTrigger>
            <TabsTrigger 
              value="projects" 
              className={`flex items-center space-x-1 sm:space-x-2 data-[state=active]:bg-primary data-[state=active]:text-white ${isRTL ? 'flex-row-reverse' : ''}`}
            >
              <Award className="h-3 w-3 sm:h-4 sm:w-4" />
              <span className="text-xs sm:text-sm">{t('admin.clients.projects') || 'المشاريع'}</span>
              <span className="bg-white/20 text-white px-1.5 py-0.5 rounded-full text-xs font-semibold">
                {projects.length}
              </span>
            </TabsTrigger>
          </TabsList>

          {/* Overview Tab */}
          <TabsContent value="overview" className="space-y-4 sm:space-y-6">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6">
              {/* Enhanced User Info Card */}
              <Card className="bg-white dark:bg-slate-800 border-slate-200 dark:border-slate-700 shadow-lg hover:shadow-xl transition-all duration-300 overflow-hidden group">
                <CardHeader className="pb-3 sm:pb-4 bg-gradient-to-r from-slate-50 to-white dark:from-slate-800 dark:to-slate-800 group-hover:bg-gradient-to-r group-hover:from-primary/5 group-hover:to-transparent transition-all duration-300">
                  <CardTitle className="text-sm sm:text-base lg:text-lg flex items-center gap-2 text-slate-900 dark:text-slate-100">
                    <div className="w-5 h-5 sm:w-6 sm:h-6 lg:w-8 lg:h-8 bg-primary/10 rounded-lg flex items-center justify-center">
                      <User size={12} className="text-primary" />
                    </div>
                    {t('admin.clients.userInfo') || 'معلومات المستخدم'}
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-3 sm:space-y-4 pt-3 sm:pt-4">
                  {isLoading ? (
                    <div className="space-y-2 sm:space-y-3">
                      <Skeleton className="h-3 sm:h-4 w-20 sm:w-24" />
                      <Skeleton className="h-3 sm:h-4 w-full" />
                      <Skeleton className="h-3 sm:h-4 w-3/4" />
                    </div>
                  ) : (
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
                      <div className="group/item">
                        <Label className="text-xs sm:text-sm font-medium text-slate-500 dark:text-slate-400 mb-1 block">
                          {t('admin.clients.name') || 'الاسم'}
                        </Label>
                        <p className="text-xs sm:text-sm font-semibold mt-1 group-hover/item:text-primary transition-colors break-words">
                          {userData?.name || responseData?.name || (clientDetails.data as any).name}
                        </p>
                      </div>
                      <div className="group/item">
                        <Label className="text-xs sm:text-sm font-medium text-slate-500 dark:text-slate-400 mb-1 block">
                          {t('admin.clients.email') || 'البريد الإلكتروني'}
                        </Label>
                        <p className="text-xs sm:text-sm mt-1 flex items-center gap-1 sm:gap-2 group-hover/item:text-primary transition-colors break-words">
                          <Mail size={10} className="text-slate-400 group-hover/item:text-primary flex-shrink-0" />
                          <span className="truncate">{userData?.email || responseData?.email || (clientDetails.data as any).email}</span>
                        </p>
                      </div>
                      <div className="group/item">
                        <Label className="text-xs sm:text-sm font-medium text-slate-500 dark:text-slate-400 mb-1 block">
                          {t('admin.clients.userType') || 'نوع المستخدم'}
                        </Label>
                        <p className="text-xs sm:text-sm mt-1 px-2 sm:px-3 py-1 bg-primary/10 text-primary rounded-full text-xs font-medium inline-block">
                          {userData?.type || 'client'}
                        </p>
                      </div>
                      {userData?.created_at && (
                        <div className="group/item">
                          <Label className="text-xs sm:text-sm font-medium text-slate-500 dark:text-slate-400 mb-1 block">
                            {t('admin.clients.userCreatedAt') || 'تاريخ إنشاء المستخدم'}
                          </Label>
                          <p className="text-xs sm:text-sm mt-1 flex items-center gap-1 sm:gap-2 group-hover/item:text-primary transition-colors">
                            <CalendarIcon size={10} className="text-slate-400 group-hover/item:text-primary flex-shrink-0" />
                            {(() => {
                              try {
                                const date = new Date(userData.created_at);
                                if (isNaN(date.getTime())) return userData.created_at;
                                return format(date, "PPP", { locale: isRTL ? ar : enUS });
                              } catch {
                                return userData.created_at;
                              }
                            })()}
                          </p>
                        </div>
                      )}
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Enhanced Client Info Card */}
              <Card className="bg-white dark:bg-slate-800 border-slate-200 dark:border-slate-700 shadow-lg hover:shadow-xl transition-all duration-300 overflow-hidden group">
                <CardHeader className="pb-3 sm:pb-4 bg-gradient-to-r from-slate-50 to-white dark:from-slate-800 dark:to-slate-800 group-hover:bg-gradient-to-r group-hover:from-primary/5 group-hover:to-transparent transition-all duration-300">
                  <CardTitle className="text-sm sm:text-base lg:text-lg flex items-center gap-2 text-slate-900 dark:text-slate-100">
                    <div className="w-5 h-5 sm:w-6 sm:h-6 lg:w-8 lg:h-8 bg-primary/10 rounded-lg flex items-center justify-center">
                      <Building2 size={12} className="text-primary" />
                    </div>
                    {t('admin.clients.clientInfo') || 'معلومات العميل'}
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-3 sm:space-y-4 pt-3 sm:pt-4">
                  {isLoading ? (
                    <div className="space-y-2 sm:space-y-3">
                      <Skeleton className="h-3 sm:h-4 w-28 sm:w-32" />
                      <Skeleton className="h-3 sm:h-4 w-full" />
                      <Skeleton className="h-3 sm:h-4 w-3/4" />
                    </div>
                  ) : (
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
                      {clientData?.company_name && (
                        <div className="group/item">
                          <Label className="text-xs sm:text-sm font-medium text-slate-500 dark:text-slate-400 mb-1 block">
                            {t('admin.clients.companyName') || 'اسم الشركة'}
                          </Label>
                          <p className="text-xs sm:text-sm mt-1 flex items-center gap-1 sm:gap-2 group-hover/item:text-primary transition-colors break-words">
                            <Building2 size={10} className="text-slate-400 group-hover/item:text-primary flex-shrink-0" />
                            {clientData.company_name}
                          </p>
                        </div>
                      )}
                      {clientData?.phone && (
                        <div className="group/item">
                          <Label className="text-xs sm:text-sm font-medium text-slate-500 dark:text-slate-400 mb-1 block">
                            {t('admin.clients.phone') || 'رقم الهاتف'}
                          </Label>
                          <p className="text-xs sm:text-sm mt-1 flex items-center gap-1 sm:gap-2 group-hover/item:text-primary transition-colors break-words">
                            <Phone size={10} className="text-slate-400 group-hover/item:text-primary flex-shrink-0" />
                            {clientData.phone}
                          </p>
                        </div>
                      )}
                      {clientData?.license_number && (
                        <div className="group/item">
                          <Label className="text-xs sm:text-sm font-medium text-slate-500 dark:text-slate-400 mb-1 block">
                            {t('admin.clients.licenseNumber') || 'رقم الرخصة'}
                          </Label>
                          <p className="text-xs sm:text-sm mt-1 flex items-center gap-1 sm:gap-2 group-hover/item:text-primary transition-colors break-words">
                            <FileText size={10} className="text-slate-400 group-hover/item:text-primary flex-shrink-0" />
                            {clientData.license_number}
                          </p>
                        </div>
                      )}
                      {clientData?.tax_id && (
                        <div className="group/item">
                          <Label className="text-xs sm:text-sm font-medium text-slate-500 dark:text-slate-400 mb-1 block">
                            {t('admin.clients.taxId') || 'الرقم الضريبي'}
                          </Label>
                          <p className="text-xs sm:text-sm mt-1 flex items-center gap-1 sm:gap-2 group-hover/item:text-primary transition-colors break-words">
                            <Hash size={10} className="text-slate-400 group-hover/item:text-primary flex-shrink-0" />
                            {clientData.tax_id}
                          </p>
                        </div>
                      )}
                      {clientData?.address && (
                        <div className="col-span-1 sm:col-span-2 group/item">
                          <Label className="text-xs sm:text-sm font-medium text-slate-500 dark:text-slate-400 mb-1 block">
                            {t('admin.clients.address') || 'العنوان'}
                          </Label>
                          <p className="text-xs sm:text-sm mt-1 flex items-start gap-1 sm:gap-2 group-hover/item:text-primary transition-colors">
                            <MapPin size={10} className="mt-0.5 flex-shrink-0 text-slate-400 group-hover/item:text-primary" />
                            <span className="line-clamp-2 break-words">{clientData.address}</span>
                          </p>
                        </div>
                      )}
                      {clientData?.created_at && (
                        <div className="group/item">
                          <Label className="text-xs sm:text-sm font-medium text-slate-500 dark:text-slate-400 mb-1 block">
                            {t('admin.clients.clientCreatedAt') || 'تاريخ إنشاء العميل'}
                          </Label>
                          <p className="text-xs sm:text-sm mt-1 flex items-center gap-1 sm:gap-2 group-hover/item:text-primary transition-colors">
                            <CalendarIcon size={10} className="text-slate-400 group-hover/item:text-primary flex-shrink-0" />
                            {(() => {
                              try {
                                const date = new Date(clientData.created_at);
                                if (isNaN(date.getTime())) return clientData.created_at;
                                return format(date, "PPP", { locale: isRTL ? ar : enUS });
                              } catch {
                                return clientData.created_at;
                              }
                            })()}
                          </p>
                        </div>
                      )}
                      <div className="group/item">
                        <Label className="text-xs sm:text-sm font-medium text-slate-500 dark:text-slate-400 mb-1 block">
                          {t('common.status') || 'الحالة'}
                        </Label>
                        <div className="mt-1">{getStatusBadge(clientStatus)}</div>
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* Enhanced Specializations Tab */}
          <TabsContent value="specializations" className="space-y-4 sm:space-y-6">
            <Card className="bg-white dark:bg-slate-800 border-slate-200 dark:border-slate-700 shadow-lg hover:shadow-xl transition-all duration-300">
              <CardHeader className="pb-3 sm:pb-4 bg-gradient-to-r from-slate-50 to-white dark:from-slate-800 dark:to-slate-800">
                <CardTitle className="text-sm sm:text-base lg:text-lg flex items-center gap-2 text-slate-900 dark:text-slate-100">
                  <div className="w-5 h-5 sm:w-6 sm:h-6 lg:w-8 lg:h-8 bg-primary/10 rounded-lg flex items-center justify-center">
                    <Briefcase size={12} className="text-primary" />
                  </div>
                  {t('admin.clients.specializations') || 'التخصصات'} 
                  <span className="bg-primary/10 text-primary px-1.5 sm:px-2 lg:px-3 py-1 rounded-full text-xs sm:text-sm font-semibold">
                    {specializations.length}
                  </span>
                </CardTitle>
              </CardHeader>
              <CardContent>
                {Array.isArray(specializations) && specializations.length > 0 ? (
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4">
                    {specializations.map((spec: any) => {
                      const specName = typeof spec.name === 'string' 
                        ? spec.name 
                        : spec.name?.[language] || spec.name?.en || '';
                      return (
                        <div key={spec.id} className="group relative p-3 sm:p-4 border border-slate-200 dark:border-slate-600 rounded-lg hover:border-primary/30 hover:shadow-lg transition-all duration-300 bg-gradient-to-br from-white to-slate-50 dark:from-slate-800 dark:to-slate-900 hover:from-primary/5 hover:to-transparent">
                          {/* Decorative gradient overlay */}
                          <div className="absolute inset-0 bg-gradient-to-br from-primary/5 to-transparent rounded-lg sm:rounded-xl opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                          
                          <div className="relative flex items-center gap-2 sm:gap-3 md:gap-4">
                            <div className="w-8 h-8 sm:w-10 sm:h-10 bg-gradient-to-br from-primary to-primary/80 rounded-lg flex items-center justify-center shadow-lg group-hover:scale-110 transition-transform duration-300 flex-shrink-0">
                              <Briefcase size={12} className="text-white" />
                            </div>
                            <div className="flex-1 min-w-0">
                              <p className="font-semibold text-xs sm:text-sm text-slate-900 dark:text-slate-100 group-hover:text-primary transition-colors truncate">
                                {specName}
                              </p>
                              {spec.description && (
                                <p className="text-xs sm:text-sm text-slate-500 dark:text-slate-400 mt-1 line-clamp-2">
                                  {typeof spec.description === 'string' 
                                    ? spec.description 
                                    : spec.description?.[language] || spec.description?.en || ''}
                                </p>
                              )}
                            </div>
                          </div>
                          
                          {/* Hover indicator */}
                          <div className="absolute -top-1 -right-1 w-2 h-2 sm:w-3 sm:h-3 bg-primary rounded-full opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <div className="text-center py-8 sm:py-12 md:py-16 text-slate-500 dark:text-slate-400">
                    <div className="w-12 h-12 sm:w-16 sm:h-16 bg-slate-100 dark:bg-slate-800 rounded-full flex items-center justify-center mx-auto mb-3 sm:mb-4">
                      <Briefcase size={20} className="text-slate-400" />
                    </div>
                    <p className="text-sm sm:text-base font-medium mb-2">{t('admin.clients.noSpecializations') || 'لا توجد تخصصات'}</p>
                    <p className="text-xs sm:text-sm text-slate-400">{t('admin.clients.noSpecializationsDesc') || 'لم يقم العميل بإضافة أي تخصصات بعد'}</p>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Documents Tab */}
          <TabsContent value="documents" className="space-y-4 sm:space-y-6">
            <Card className="bg-white dark:bg-slate-800 border-slate-200 dark:border-slate-700">
              <CardHeader className="pb-3 sm:pb-4">
                <CardTitle className="text-sm sm:text-base lg:text-lg flex items-center gap-2 text-slate-900 dark:text-slate-100">
                  <FileText size={12} className="text-foreground" />
                  {t('admin.clients.documents') || 'الوثائق'} ({documents.length})
                </CardTitle>
              </CardHeader>
              <CardContent>
                {Array.isArray(documents) && documents.length > 0 ? (
                  <ScrollArea className="h-48 sm:h-64 md:h-96">
                    <div className="space-y-2 sm:space-y-3">
                      {documents.map((doc: any) => (
                        <div key={doc.id} className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between p-3 sm:p-4 border border-slate-200 dark:border-slate-600 rounded-lg hover:bg-slate-50 dark:hover:bg-slate-700 transition-colors">
                          <div className="flex items-center gap-2 sm:gap-3 min-w-0 flex-1">
                            <div className={`w-8 h-8 sm:w-10 sm:h-10 rounded-lg flex items-center justify-center flex-shrink-0 ${
                              doc.status === 'approved' 
                                ? 'bg-green-100 dark:bg-green-900' 
                                : doc.status === 'rejected'
                                ? 'bg-red-100 dark:bg-red-900'
                                : 'bg-slate-100 dark:bg-slate-700'
                            }`}>
                              <FileText size={14} className={
                                doc.status === 'approved' 
                                  ? 'text-green-600 dark:text-green-300' 
                                  : doc.status === 'rejected'
                                  ? 'text-red-600 dark:text-red-300'
                                  : 'text-foreground'
                              } />
                            </div>
                            <div className="min-w-0 flex-1">
                              <p className="font-semibold text-xs sm:text-sm text-slate-900 dark:text-slate-100 leading-tight truncate">
                                {doc.original_name || doc.title}
                              </p>
                              <div className="flex flex-wrap items-center gap-1 sm:gap-2 text-xs text-slate-500 dark:text-slate-400 mt-1 sm:mt-2">
                                <span className="flex items-center gap-1 bg-slate-100 dark:bg-slate-700 px-1.5 sm:px-2 py-1 rounded">
                                  <FileText size={8} />
                                  <span className="font-semibold text-xs truncate">{doc.type}</span>
                                </span>
                                <span className="bg-blue-100 dark:bg-blue-900 text-blue-700 dark:text-blue-300 px-1.5 sm:px-2 py-1 rounded">
                                  {(doc.file_size / 1024).toFixed(1)} KB
                                </span>
                                <span className={`px-1.5 sm:px-2 py-1 rounded-full font-medium truncate ${
                                  doc.status === 'approved' 
                                    ? 'bg-green-100 text-green-700 dark:bg-green-900 dark:text-green-300' 
                                    : doc.status === 'rejected'
                                    ? 'bg-red-100 text-red-700 dark:bg-red-900 dark:text-red-300'
                                    : 'bg-amber-100 text-amber-700 dark:bg-amber-900 dark:text-amber-300'
                                }`}>
                                  {doc.status === 'approved' 
                                    ? (t('profile.approved') || 'معتمد')
                                    : doc.status === 'rejected'
                                    ? (t('profile.rejected') || 'مرفوض')
                                    : (t('profile.pending') || 'قيد المراجعة')
                                  }
                                </span>
                              </div>
                            </div>
                          </div>
                          <div className="flex items-center gap-1 sm:gap-2 flex-shrink-0">
                            {/* Approval Buttons */}
                            {doc.status !== 'approved' && (
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => handleApproveDocument(doc.id, 'approved')}
                                className="text-xs text-green-600 border-green-200 hover:bg-green-50 px-1.5 sm:px-2"
                              >
                                <Check size={10} className="mr-1" />
                                <span className="hidden xs:inline">{t('admin.clients.approve') || 'اعتماد'}</span>
                                <span className="xs:hidden">{t('admin.clients.approve')?.substring(0, 4) || 'اعتم'}</span>
                              </Button>
                            )}
                            {doc.status === 'approved' && (
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => handleApproveDocument(doc.id, 'rejected')}
                                className="text-xs text-red-600 border-red-200 hover:bg-red-50 px-1.5 sm:px-2"
                              >
                                <XCircle size={10} className="mr-1" />
                                <span className="hidden xs:inline">{t('admin.clients.reject') || 'رفض الاعتماد'}</span>
                                <span className="xs:hidden">{t('admin.clients.reject')?.substring(0, 4) || 'رفض'}</span>
                              </Button>
                            )}
                            <Button
                              variant="outline"
                              size="xs"
                              onClick={() => {
                                const downloadUrl = doc.file_url || `${window.location.origin}/api/v1/files/${doc.file_path}`;
                                window.open(downloadUrl, '_blank');
                              }}
                              className="text-xs px-1.5"
                            >
                              <Download size={8} className="mr-1" />
                              <span className="hidden xs:inline">{t('common.download') || 'تحميل'}</span>
                              <span className="xs:hidden">{t('common.download')?.substring(0, 4) || 'تحم'}</span>
                            </Button>
                          </div>
                        </div>
                        
                      ))}
                      
                    </div>
                  </ScrollArea>
                ) : (
                  <div className="text-center py-6 sm:py-8 md:py-12 text-slate-500 dark:text-slate-400">
                    <FileText size={32} className="mx-auto mb-2 sm:mb-3 md:mb-4 opacity-50 text-foreground" />
                    <p className="text-sm sm:text-base">{t('admin.clients.noDocuments') || 'لا توجد وثائق'}</p>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Projects Tab */}
          <TabsContent value="projects" className="space-y-4 sm:space-y-6">
            <Card className="bg-white dark:bg-slate-800 border-slate-200 dark:border-slate-700">
              <CardHeader className="pb-3 sm:pb-4">
                <CardTitle className="text-sm sm:text-base lg:text-lg flex items-center gap-2 text-slate-900 dark:text-slate-100">
                  <Award size={12} className="text-foreground" />
                  {t('admin.clients.projects') || 'المشاريع'} ({projects.length})
                </CardTitle>
              </CardHeader>
              <CardContent>
                {Array.isArray(projects) && projects.length > 0 ? (
                  <ScrollArea className="h-48 sm:h-64 md:h-96">
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
                      {projects.map((project: any) => {
                        const projectTitle = typeof project.title === 'string' 
                          ? project.title 
                          : project.title?.[language] || project.title?.en || '';
                        const projectDescription = typeof project.description === 'string'
                          ? project.description
                          : project.description?.[language] || project.description?.en || '';
                        const projectLocation = typeof project.location === 'string'
                          ? project.location
                          : project.location?.[language] || project.location?.en || '';
                        
                        return (
                          <div key={project.id} className="p-2 sm:p-3 md:p-4 border border-slate-200 dark:border-slate-600 rounded-lg hover:bg-slate-50 dark:hover:bg-slate-700 transition-colors">
                            {/* Project Images */}
                            {project.images && project.images.length > 0 && (
                              <div className="mb-2 sm:mb-3">
                                <div className="flex gap-1 sm:gap-2 overflow-x-auto">
                                  {project.images.slice(0, 3).map((image: any, index: number) => (
                                    <div key={image.id} className="relative flex-shrink-0">
                                      <div className="w-10 h-10 sm:w-14 sm:h-14 md:w-20 md:h-20 rounded-lg overflow-hidden border border-slate-200 dark:border-slate-600">
                                        <OptimizedImage
                                          src={image.url}
                                          alt={`${projectTitle} - Image ${index + 1}`}
                                          containerClassName="w-full h-full"
                                          className="w-full h-full object-cover"
                                        />
                                      </div>
                                      {project.images.length > 3 && index === 2 && (
                                        <div className="absolute inset-0 bg-black/60 rounded-lg flex items-center justify-center">
                                          <span className="text-white font-semibold text-xs sm:text-sm">
                                            +{project.images.length - 3}
                                          </span>
                                        </div>
                                      )}
                                    </div>
                                  ))}
                                </div>
                                <p className="text-xs text-slate-500 dark:text-slate-400 mt-1">
                                  {project.images.length} {project.images.length === 1 ? 'صورة' : project.images.length === 2 ? 'صورتان' : 'صور'}
                                </p>
                              </div>
                            )}
                            
                            <div className="flex flex-col gap-1.5 sm:gap-2">
                              <div className="flex items-start justify-between gap-2">
                                <h4 className="font-semibold text-xs sm:text-sm md:text-base text-slate-900 dark:text-slate-100 truncate flex-1">{projectTitle}</h4>
                                <div className="flex-shrink-0">
                                  <Badge variant="outline" className="text-xs">
                                    {project.status || 'completed'}
                                  </Badge>
                                </div>
                              </div>
                              {projectDescription && (
                                <p className="text-xs sm:text-sm text-slate-500 dark:text-slate-400 line-clamp-2">
                                  {projectDescription}
                                </p>
                              )}
                              <div className="flex flex-wrap items-center gap-1.5 sm:gap-2 text-xs text-slate-500 dark:text-slate-400">
                                {projectLocation && (
                                  <span className="flex items-center gap-1 truncate max-w-[100px] xs:max-w-none">
                                    <MapPin size={8} className="flex-shrink-0" />
                                    <span className="truncate">{projectLocation}</span>
                                  </span>
                                )}
                                {project.completed_date && (
                                  <span className="flex items-center gap-1 truncate max-w-[120px] xs:max-w-none">
                                    <CalendarIcon size={8} className="flex-shrink-0" />
                                    <span className="truncate">
                                      {(() => {
                                        try {
                                          const date = new Date(project.completed_date);
                                          if (isNaN(date.getTime())) return project.completed_date;
                                          return format(date, "PPP", { locale: isRTL ? ar : enUS });
                                        } catch {
                                          return project.completed_date;
                                        }
                                      })()}
                                    </span>
                                  </span>
                                )}
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </ScrollArea>
                ) : (
                  <div className="text-center py-6 sm:py-8 md:py-12 text-slate-500 dark:text-slate-400">
                    <Award size={32} className="mx-auto mb-2 sm:mb-3 md:mb-4 opacity-50 text-foreground" />
                    <p className="text-xs sm:text-sm md:text-base">{t('admin.clients.noProjects') || 'لا توجد مشاريع'}</p>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
};

export default AdminClientDetails;
